/**
 * Briefs build by themselves (Sofia, Nov 4 2026: "Ich will keine Wartezeit da haben"):
 *
 *  - `buildBriefOnce`: one build per role at a time on this page. The background build of the list
 *    and the build on opening the role share it, so the model runs once, not twice.
 *  - `usePrebuildBriefs`: on the campaign list, EVERY role with a posting and no brief is built in
 *    the background, 3 at a time (Sofia, Nov 4 2026: a build takes ~16 s, opening must not wait) —
 *    only with Google connected, so the kick-off sheet is read too. The brief is shared, so whoever
 *    opens the list first builds it for everyone. A role is tried once per page load: a failed one (no posting in Lever, no sheet) is
 *    left for the Brief step, which says why.
 *
 * The brief is the role's, stored on the server and shared by every recruiter; a build of an
 * unchanged role returns the stored brief without a model call.
 */
import { useEffect, useRef } from 'react';
import * as api from '../../services/taOutreachApi';
import type { Campaign } from '../../types/taOutreach';
import { runInLanes } from './updateAll';

type Built = Awaited<ReturnType<typeof api.compileBrief>>;

const inFlight = new Map<string, Promise<Built>>();
const tried = new Set<string>();

/** The role's brief, built (or the stored one returned); a build already running is joined. */
export function buildBriefOnce(postingId: string, token: string | null): Promise<Built> {
  const running = inFlight.get(postingId);
  if (running) return running;
  const p = api.compileBrief(postingId, token, false).finally(() => inFlight.delete(postingId));
  inFlight.set(postingId, p);
  return p;
}

/** Whether a build for the role is running on this page right now. */
export const isBuilding = (postingId: string) => inFlight.has(postingId);

/** Tests only. */
export function _resetBriefBuilds() {
  inFlight.clear();
  tried.clear();
}

export function usePrebuildBriefs({
  campaigns,
  myEmail,
  token,
  onBuilt,
}: {
  campaigns: Campaign[];
  myEmail: string;
  token: string | null;
  onBuilt: (postingId: string) => void;
}) {
  const built = useRef(onBuilt);
  useEffect(() => {
    built.current = onBuilt;
  }, [onBuilt]);
  useEffect(() => {
    if (!token || !myEmail) return;
    const ids = campaigns
      .filter((c) => c.postingId && c.hasBrief === false && !tried.has(c.postingId))
      .map((c) => c.postingId as string);
    if (!ids.length) return;
    ids.forEach((id) => tried.add(id));
    void runInLanes(
      ids,
      async (id) => {
        try {
          await buildBriefOnce(id, token);
          built.current(id);
        } catch {
          // Left for the Brief step: opening the role builds again and shows why it cannot.
        }
      },
      3
    );
  }, [campaigns, myEmail, token]);
}
